import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AppService } from "../../../app.service";
import { NotificationService } from "../../../core/services/notification.service";
import { ComponentPageBase } from "../../../shared/components/component-page-base/component-page-base";
import { DaDataAddressComponent } from "../../../shared/components/dadata-address/dadata-address.component";
import { DropdownListCountryPhoneMasksComponent } from "../../../shared/components/dropdownlist-country-phone-masks/dropdownlist-country-phone-masks.component";
import { CategoryType } from "../../../shared/enums/category-type.enum";
import { EntityViewModel } from "../../../shared/models/core/EntityViewModel";
import { KeyValueObject } from "../../../shared/models/core/KeyValueObject";
import { AddressSuggestion } from "../../../shared/models/dadata-address/address-suggestion";
import { ServiceCenterDto } from "../../../shared/models/service-center/service-center-dto";
import { ServiceCenterEmployee } from "../../../shared/models/service-center/service-center-employee";
import { LookupService } from "../../../shared/services/lookup.service";
import { ServiceCentersService } from "../../../shared/services/service-centers.service";

@Component({
	selector: 'service-center-page',
	templateUrl: './service-center.page.html',
	styleUrls: [
		'./service-center.page.scss',
		'../../../../vendor/libs/angular2-ladda/angular2-ladda.scss'
	]
})
export class ServiceCenterPage extends ComponentPageBase<ServiceCenterDto> implements OnInit, AfterViewInit {

	@ViewChild('countryPhoneMasksComponent') countryPhoneMasksComponent: DropdownListCountryPhoneMasksComponent;
	@ViewChild('dadataAddressControl') dadataAddressControl: DaDataAddressComponent;

	public activeIdString: any = "main";
	public categoryTypeId = <number>CategoryType.serviceCenters;

	cellPhoneMask: string = "+7 (000) 000-00-00";
	public rules: { [key: string]: RegExp } = {
		'0': /\d/
	};

	serviceCenterTypes: KeyValueObject[] = [];
	parentServiceCenters: KeyValueObject[] = [];
	users: KeyValueObject[] = [];
	employeeCandidates: ServiceCenterEmployee[] = [];
	availableEmployeeUsers: ServiceCenterEmployee[] = [];
	isEmployeeRowEditing = false;

	parentDefaultItem: KeyValueObject = { id: null, name: '(Не выбран)' } as KeyValueObject;
	userDefaultItem: KeyValueObject = { id: null, name: '(Не выбран)' } as KeyValueObject;
	employeeDefaultItem: ServiceCenterEmployee = {
		userId: null,
		fullName: '(Не выбран)',
		roles: '',
		performerGroups: ''
	};

	constructor(
		protected route: ActivatedRoute,
		protected router: Router,
		protected appService: AppService,
		protected notificationService: NotificationService,
		protected dataService: ServiceCentersService,
		private lookupService: LookupService
	) {
		super(route, router, appService, notificationService, dataService);
		this.listPageRoutePath = 'categories/service-centers';
		this.appService.pageTitle = 'Новый сервисный центр';
	}

	ngOnInit(): void {
		this.entityId = this.route.snapshot.paramMap.get('serviceCenterId');

		this.lookupService.getData('service-center-types', null).subscribe(data => this.serviceCenterTypes = data || []);
		this.lookupService.getData('service-centers', null).subscribe(data => {
			const currentId = this.entityId ? +this.entityId : null;
			this.parentServiceCenters = (data || []).filter(x => !x.skipInventory && x.id !== currentId);
		});
		this.lookupService.getData('users', null).subscribe(data => this.users = data || []);
		this.dataService.getEmployeeCandidates().subscribe(data => {
			this.employeeCandidates = data || [];
			this.refreshAvailableEmployeeUsers();
		});

		if (this.entityId != null) {
			this.dataService.getById(+this.entityId)
				.subscribe(vm => {
					this.entityName = `${vm.entity.name}`;
					this.dataBind(vm);
					if (!this.entityViewModel.entity.employees) {
						this.entityViewModel.entity.employees = [];
					}
					setTimeout(() => {
						this.refreshMask();
						this.initAddressControl();
					}, 100);
				});
		} else {
			this.entityViewModel = new EntityViewModel<ServiceCenterDto>();
			this.entityViewModel.entity = new ServiceCenterDto();
			this.entityViewModel.entity.serviceCenterTypeId = 1;
			this.entityViewModel.entity.employees = [];
			this.entityViewModel.entity.createdDate = new Date();
			this.canEditForm = true;
			this.isNew = true;
		}
	}

	ngAfterViewInit(): void {
		this.refreshMask();
		this.initAddressControl();
	}

	setMask(mask: string) {
		this.cellPhoneMask = mask;
		if (this.entityViewModel && this.entityViewModel.entity) {
			this.entityViewModel.entity.phone = '';
		}
	}

	refreshMask() {
		if (!this.countryPhoneMasksComponent || !this.entityViewModel || !this.entityViewModel.entity) {
			return;
		}
		this.cellPhoneMask = this.countryPhoneMasksComponent.getAndSetPhoneMask(this.entityViewModel.entity.phone);
	}

	get selectedType(): KeyValueObject {
		if (!this.entityViewModel || !this.entityViewModel.entity) {
			return null;
		}
		return this.serviceCenterTypes.find(x => x.id === this.entityViewModel.entity.serviceCenterTypeId);
	}

	get isSkipInventoryType(): boolean {
		return !!(this.selectedType && this.selectedType.skipInventory);
	}

	onAddressChange(value: AddressSuggestion) {
		if (!this.entityViewModel || !this.entityViewModel.entity) {
			return;
		}

		const entity = this.entityViewModel.entity;
		const nextAddress = value && value.address ? value.address : null;
		const addressChanged = entity.address !== nextAddress;

		entity.address = nextAddress;
		if (addressChanged) {
			if (value && value.latitude != null && value.longitude != null) {
				entity.latitude = value.latitude;
				entity.longitude = value.longitude;
			} else {
				entity.latitude = null;
				entity.longitude = null;
			}
			this.markFormDirty();
		} else if (value && value.latitude != null && value.longitude != null) {
			entity.latitude = value.latitude;
			entity.longitude = value.longitude;
		}
	}

	onTypeChange(): void {
		if (!this.isSkipInventoryType) {
			this.entityViewModel.entity.parentServiceCenterId = null;
			this.entityViewModel.entity.inventoryResponsibleUserId = null;
		}
		this.entityViewModel.entity.skipInventory = this.isSkipInventoryType;
	}

	createNewEmployee(): ServiceCenterEmployee {
		return {
			userId: null,
			fullName: '',
			roles: '',
			performerGroups: '',
			isNew: true
		};
	}

	addEmployeeRow(): void {
		if (!this.canEditForm || this.isEmployeeRowEditing) {
			return;
		}

		this.entityViewModel.entity.employees = this.entityViewModel.entity.employees || [];
		this.refreshAvailableEmployeeUsers();
		this.entityViewModel.entity.employees.unshift(this.createNewEmployee());
		this.isEmployeeRowEditing = true;
		this.markFormDirty();
	}

	onEmployeeFilterChange(searchValue: string, currentUserId: number = null): void {
		this.refreshAvailableEmployeeUsers(searchValue, currentUserId);
	}

	onEmployeeUserChange(userId: number, dataItem: ServiceCenterEmployee): void {
		if (!dataItem) {
			return;
		}

		dataItem.userId = userId;
		this.applyEmployeeCandidate(dataItem);
	}

	commitEmployee(employee: ServiceCenterEmployee): void {
		if (!employee || !employee.userId) {
			return;
		}

		this.applyEmployeeCandidate(employee);
		employee.isNew = false;
		this.isEmployeeRowEditing = false;
		this.markFormDirty();
	}

	cancelEmployee(index: number): void {
		const employees = this.entityViewModel.entity.employees || [];
		if (index >= 0 && index < employees.length) {
			employees.splice(index, 1);
		}
		this.isEmployeeRowEditing = false;
		this.markFormDirty();
	}

	removeEmployee(index: number): void {
		const employees = this.entityViewModel.entity.employees || [];
		if (index >= 0 && index < employees.length) {
			employees.splice(index, 1);
		}
		this.markFormDirty();
	}

	get canSaveCard(): boolean {
		const entity = this.entityViewModel && this.entityViewModel.entity;
		if (!entity) {
			return false;
		}

		const hasName = !!(entity.name && entity.name.trim());
		if (!hasName || !this.hasValidPhone || !entity.serviceCenterTypeId) {
			return false;
		}

		if (this.isSkipInventoryType) {
			if (!entity.parentServiceCenterId || !entity.inventoryResponsibleUserId) {
				return false;
			}
		}

		const pendingEmployee = (entity.employees || []).some(x => x.isNew);
		return !pendingEmployee && !this.isEmployeeRowEditing;
	}

	get hasValidPhone(): boolean {
		const phone = this.entityViewModel && this.entityViewModel.entity && this.entityViewModel.entity.phone;
		if (!phone) {
			return false;
		}
		return phone.replace(/\D/g, '').length >= 10;
	}

	beforeSaveChanges() {
		this.entityViewModel.entity.employees = (this.entityViewModel.entity.employees || [])
			.filter(x => x.userId && !x.isNew);
	}

	afterSaveChanges() {
		if (this.isNew) {
			this.router.navigate([this.listPageRoutePath]);
			return;
		}

		const id = +(this.entityIdConst || this.entityId);
		if (!id) {
			return;
		}

		this.dataService.getById(id).subscribe(vm => {
			this.entityName = `${vm.entity.name}`;
			this.dataBind(vm);
			if (!this.entityViewModel.entity.employees) {
				this.entityViewModel.entity.employees = [];
			}
			this.isEmployeeRowEditing = false;
			setTimeout(() => {
				this.refreshMask();
				this.initAddressControl();
			}, 100);
		});
	}

	private applyEmployeeCandidate(employee: ServiceCenterEmployee): void {
		const candidate = this.employeeCandidates.find(x => x.userId === employee.userId);
		if (!candidate) {
			employee.fullName = '';
			employee.roles = '';
			employee.performerGroups = '';
			return;
		}

		employee.fullName = candidate.fullName;
		employee.roles = candidate.roles;
		employee.performerGroups = candidate.performerGroups;
	}

	private refreshAvailableEmployeeUsers(searchValue: string = '', currentUserId: number = null): void {
		const usedIds = (this.entityViewModel && this.entityViewModel.entity && this.entityViewModel.entity.employees || [])
			.map(x => x.userId)
			.filter(id => !!id && id !== currentUserId);
		const source = (this.employeeCandidates || []).filter(x => usedIds.indexOf(x.userId) < 0);
		const term = (searchValue || '').trim().toLowerCase();
		this.availableEmployeeUsers = term
			? source.filter(x => (x.fullName || '').toLowerCase().indexOf(term) >= 0)
			: source.slice();
	}

	private initAddressControl(): void {
		if (!this.dadataAddressControl || !this.entityViewModel || !this.entityViewModel.entity) {
			return;
		}
		this.dadataAddressControl.initValue(this.entityViewModel.entity.address);
	}

	private markFormDirty(): void {
		if (this.entityForm) {
			this.entityForm.form.markAsDirty();
		}
	}
}
